import { Box } from '@chakra-ui/react';
import type { Mention } from '@fluenceur/types';
import { parseAsString, useQueryState } from 'nuqs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import MentionMediaThumbnail from './MentionMediaThumbnail';
import { MentionBoostedBadge } from './MentionBoostedBadge';
import MentionCardPlatformChip from './MentionCardPlatformChip';
import MentionCardFooter from './MentionCardFooter';

interface MentionCardProps {
  mention: Mention;
  showCreator?: boolean;
  /** Denser, flat variant for narrow contexts like the contact drawer. */
  compact?: boolean;
  /** Media aspect ratio (CSS aspect-ratio). Defaults to a portrait thumbnail. */
  mediaAspectRatio?: string;
}

/**
 * One tile of the mentions grid. Deliberately static: the grid is virtualized,
 * so a card mounts every time its row scrolls back into view, and an entrance
 * animation would replay on each of those mounts.
 */
const MentionCard: React.FC<MentionCardProps> = ({
  mention,
  showCreator = true,
  compact = false,
  mediaAspectRatio = '4/5',
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [, setPreviewMentionId] = useQueryState('previewMention', parseAsString);

  const { t } = useTranslation();
  const creatorHandle = mention.author.username ? `@${mention.author.username}` : 'creator';

  // Boosted state is announced on the card itself (the interactive target); the
  // overlay badge stays purely decorative.
  const previewLabel = t('mentions.card.openPreviewLabel', {
    username: creatorHandle,
    defaultValue: `Open mention preview for ${creatorHandle}`,
  });
  const ariaLabel = mention.isBoosted
    ? `${previewLabel}, ${t('mentions.boostedBadge.label')}`
    : previewLabel;

  const handleOpenMention = React.useCallback(() => {
    setPreviewMentionId(mention.id);
  }, [mention.id, setPreviewMentionId]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      handleOpenMention();
    },
    [handleOpenMention]
  );

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      bg="panelBg"
      rounded={compact ? 'sm' : 'md'}
      overflow="hidden"
      border="1px solid"
      borderColor="border"
      cursor="pointer"
      _hover={{ borderColor: 'border.emphasized' }}
      transition="border-color 0.15s"
      onClick={handleOpenMention}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Media Thumbnail */}
      <Box position="relative" aspectRatio={mediaAspectRatio} width="100%" bg="softCream">
        <MentionMediaThumbnail mention={mention} isHovered={isHovered} openOnClick={false} />
        <MentionCardPlatformChip mention={mention} compact={compact} />
        {mention.isBoosted && <MentionBoostedBadge compact={compact} />}
      </Box>

      {/* Footer Panel */}
      <MentionCardFooter mention={mention} showCreator={showCreator} compact={compact} />
    </Box>
  );
};

// Memoized: the grid re-renders on every scroll frame that changes the rendered
// rows, and a card's props only change when its mention does.
export default React.memo(MentionCard);
